import { useState } from 'react'
import { moveFinding, type FindingChange, type RegisterRow } from '../../../api/auditFindings'
import { toast } from '../../../ui/notify'
import { SeverityPill } from './SeverityPill'

/**
 * Un rand din registru: severitatea, ce e, de cand, la cine, si in ce stare.
 *
 * Se salveaza la fiecare schimbare, ca la chestionar (`audit.js`,
 * `moveFinding`). Mutarea unei scadente cere un motiv - se intreaba pe loc,
 * si daca omul renunta, campul se pune la loc: o amanare care nu se vede nu
 * e o amanare, e o uitare. Serverul refuza si el fara motiv
 * (`Findings::move`), deci intrebarea de aici nu e singura poarta.
 *
 * Cu `mayRun` fals, randul e numai de citit - aceleasi celule, fara controale.
 */
export function FindingRow({
  row,
  states,
  mayRun,
  onMoved,
}: {
  row: RegisterRow
  states: string[]
  mayRun: boolean
  /** Dupa o mutare scrisa - pagina isi reciteste cifrele de sus. */
  onMoved?: () => void
}) {
  const [state, setState] = useState(row.state)
  const [owner, setOwner] = useState(row.owner ?? '')
  const [due, setDue] = useState(row.due_on ?? '')
  const [saved, setSaved] = useState({ state: row.state, owner: row.owner ?? '', due: row.due_on ?? '' })

  async function move(change: FindingChange, revert: () => void) {
    try {
      const r = await moveFinding(row.fingerprint, change)
      if (r.ok) {
        setSaved((was) => ({
          state: change.state ?? was.state,
          owner: change.owner ?? was.owner,
          due: change.due_on ?? was.due,
        }))
        toast('Saved.', 'Findings')
        onMoved?.()
        return
      }
      revert()
      toast(r.error || 'That did not save.', 'Findings', 'error')
    } catch {
      revert()
      toast('That did not save.', 'Findings', 'error')
    }
  }

  function changeDue(value: string) {
    const was = saved.due
    setDue(value)
    if (value === was) return
    const why = window.prompt('Moving a due date needs a reason. Why is it moving?')
    if (!why) {
      setDue(was)
      return
    }
    void move({ due_on: value, why }, () => setDue(was))
  }

  return (
    <tr className={`od-audit-finding${row.overdue ? ' is-overdue' : ''}`} data-fingerprint={row.fingerprint}>
      <td className="adm-nowrap">
        <SeverityPill severity={row.severity} />
      </td>
      <td>
        <strong>{row.title}</strong>
        <div className="adm-muted">
          {row.area_code}
          {row.check_id.trim() !== '' ? <> &middot; {row.check_id}</> : null}
          &nbsp;&middot; open {row.days_open} days
          {row.machines.trim() !== '' ? <> &middot; {row.machines.slice(0, 90)}</> : null}
        </div>
        {row.moved_why.trim() !== '' ? <div className="od-audit-moved">Date moved: {row.moved_why}</div> : null}
      </td>
      <td className="adm-nowrap">
        {mayRun ? (
          <input
            type="date"
            className="form-control input-sm od-audit-due"
            value={due}
            onChange={(e) => changeDue(e.target.value)}
          />
        ) : (
          row.due_on
        )}
      </td>
      <td>
        {mayRun ? (
          <input
            type="text"
            className="form-control input-sm od-audit-owner"
            value={owner}
            placeholder="nobody yet"
            onChange={(e) => setOwner(e.target.value)}
            onBlur={() => {
              if (owner === saved.owner) return
              const was = saved.owner
              void move({ owner }, () => setOwner(was))
            }}
          />
        ) : owner.trim() !== '' ? (
          owner
        ) : (
          '—'
        )}
      </td>
      <td className="adm-nowrap">
        {mayRun ? (
          <select
            className="form-control input-sm od-audit-state"
            value={state}
            onChange={(e) => {
              const was = saved.state
              const next = e.target.value
              setState(next)
              void move({ state: next }, () => setState(was))
            }}
          >
            {states.map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </select>
        ) : (
          state
        )}
      </td>
    </tr>
  )
}
